import { map, Observable, of } from 'rxjs';

import { GenericSearchUtil } from '../utils/generic-search-util';
import { HisOption } from './his-option';
import { HisWidgetBaseSelect } from './his-widget-base-select';

export class HisSelectWidget extends HisWidgetBaseSelect<HisOption> {
    kind = 'dropdown';
    optionsKey: string;
    options: Observable<HisOption[]>;
    multiple = false;
    configFile: string;

    constructor( options: any = {} ) {
        super( options );

        this.kind = options.kind || 'dropdown';
        this.optionsKey = options.optionsKey;
        this.options = options.options || of( [] );
        this.multiple = options.multiple;
        this.configFile = options.configFile;
    }

    protected doPresetInputFields( presetValue: any, onPresetSuccess: () => void ) {
        console.log( 'presetting single-select field', this.id, 'to', presetValue );

        this.value = { id: undefined, label: undefined }; // 'undefined' is more clear as a "not selected" than 'null' here
        this.originalValue = { ...this.value };

        if ( GenericSearchUtil.isLazySelectFrom( this.optionsKey, this.showDropdownIcon ) ) {
            return;
        }
        
        if ( !presetValue ) {
            return;
        }

        if ( presetValue === 'ISNULL' && this.showNullItem ) {
            const nullOption = new HisOption( null, 'ISNULL' ); // label is set at his-search-autocomplete.component.ts

            Object.assign( this.value, nullOption );
            this.value = nullOption;
            this.originalValue = { ...this.value };

            onPresetSuccess();
            return; // we can only select one option, so we are done here
        }
        
        // Sowohl "model" (form control) und "view" auf denselben Wert zu setzen ist hier besonders tricky, weil
        // * this.value der form control zugewiesen wird bevor der folgende subscribe()-Block abgearbeitet ist
        // * this.value hier nur einen einzelnen Wert enthält (bei einem Array ist die Sache einfacher)
        // Folgender Workaround funktioniert (Schritte 1. bis 3.):
        // 1. Wert für die form control mit einem Dummy-Objekt vorbelegen (siehe oben 'this.value = ...')
        this.getWidgetValues( presetValue ).subscribe( ( val: HisOption ) => {
            if ( val ) {
                // 2. Das value-Objekt aktualisieren, dass schon der form control zugewiesen ist
                Object.assign( this.value, val );
                // 3. Um die Anzeige zu aktualisieren müssen wir ein neues Objekt erzeugen
                this.value = val;
                this.originalValue = { ...this.value };
                // Ab hier referenziert das model nicht mehr den tatsächlichen Wert!
                // Änderungen am Widget die nicht über die UI erfolgen werden nicht übernommen!

                onPresetSuccess();
            }
        } );

    }

    /**
     * IMPORTANT: This method is SPECIFICALLY designed for generic search functionality only!
     * 
     * Behavior:
     * - Will return empty/undefined if no matching option is found 
     * - For lazy-loaded selects: Returns warning that values will be loaded later (the actual values need to be handled separately in his-search-form)
     * - Primarily used in the context of generic search field 
     * 
     * 	* Limitations:
     * - Should not be used outside of generic search context
     * 
     */
    public getWidgetValueDetail( id: string ) {
        if ( GenericSearchUtil.isLazySelectFrom( this.optionsKey, this.showDropdownIcon ) ) {
            console.log( `HisSelectWidget.getWidgetValues(): Options for "${this.label}" will be loaded later in his-search-form by copying` );
        }

        return this.options.pipe(
            map( options => {
                const value = options.find( opt => opt.id === id.toString() );
                return { value, hasUnresolvedId: !value };
            } )
        );
    }
}
